import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import DescriptionIcon from 'components/material-symbol/icons/Description'
import EventIcon from 'components/material-symbol/icons/Event'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import {
  mergeDesktopDatePickerSlotProps,
  mergeDesktopDatePickerSlots,
} from 'components/pga-time-picker/desktop-date-picker'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import {
  OUTLINED_INPUT_BORDER_RADIUS,
  outlinedPickerFieldSx,
} from 'lib/form-field-styles'
import { calendarDatePickerTheme } from 'pages/pga-coach/bookings/schedule/calendar/calendar-date-picker-theme'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import {
  PURCHASE_LIMIT_PERIOD_OPTIONS,
  TURNAROUND_HOURS_OPTIONS,
  VIRTUAL_COACHING_LEGAL_CAPTION,
} from '../offering-kind'
import { optionalLabel, requiredLabel } from './required-field'

const EXPIRES_AT_DESCRIPTION =
  'The last day students can buy this offering. Leave blank if it should not expire.'

const datePickerTheme = createTheme(calendarDatePickerTheme, {
  shape: { borderRadius: OUTLINED_INPUT_BORDER_RADIUS },
})

const VirtualCoachingSettings = ({ isCreateFlow, onApplyTemplate }) => {
  const editClasses = useEditFormStyles()
  const { control, errors, setValue, watch } = useFormContext()
  const expiresAt = watch('expiresAt')

  return (
    <>
      {isCreateFlow ? (
        <Box mb={3}>
          <TipWell
            caption={
              <>
                <Box component="span" display="block" fontWeight={700}>
                  Start from a template
                </Box>
                Prefill the name, description, and turnaround time with a swing
                analysis template. You can edit everything afterwards.
              </>
            }
            action={
              <TipWellActionButton
                type="button"
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={onApplyTemplate}
                data-cy="virtualCoachingTemplate"
              >
                Use template
              </TipWellActionButton>
            }
          />
        </Box>
      ) : null}

      <Box className={editClasses.fieldGroup}>
        <Typography className={editClasses.sectionHeading}>
          Turnaround Time
        </Typography>
        <Typography className={editClasses.fieldHelper}>
          How long you commit to returning your analysis after the student
          uploads their video. Students see this before they buy.
        </Typography>
        <FormControl
          error={!!errors.turnaroundHours}
          variant="outlined"
          fullWidth
          className={editClasses.fieldInput}
        >
          <InputLabel id="turnaroundHours-label">
            {requiredLabel('Turnaround time')}
          </InputLabel>
          <Controller
            name="turnaroundHours"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelId="turnaroundHours-label"
                label={requiredLabel('Turnaround time')}
                data-cy="turnaroundHours"
                value={value ?? ''}
                onChange={onChange}
              >
                {TURNAROUND_HOURS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.turnaroundHours ? (
            <FormHelperText>Select a turnaround time</FormHelperText>
          ) : null}
        </FormControl>
      </Box>

      <Box className={editClasses.fieldGroup}>
        <Typography className={editClasses.sectionHeading}>
          {optionalLabel('Purchase Limit')}
        </Typography>
        <Typography className={editClasses.fieldHelper}>
          Limit how many of this offering can be purchased so your review queue
          stays manageable. Leave blank for no limit.
        </Typography>
        <Box display="flex" style={{ gap: 16 }}>
          <Controller
            name="purchaseLimit"
            control={control}
            render={({ onChange, onBlur, value }) => (
              <TextField
                id="purchaseLimit"
                label="Max purchases"
                data-cy="purchaseLimit"
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.purchaseLimit}
                helperText={errors.purchaseLimit?.message}
                variant="outlined"
                inputProps={{ inputMode: 'numeric' }}
                style={{ flex: 1 }}
              />
            )}
          />
          <FormControl variant="outlined" style={{ flex: 1 }}>
            <InputLabel id="purchaseLimitPeriod-label">Period</InputLabel>
            <Controller
              name="purchaseLimitPeriod"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  labelId="purchaseLimitPeriod-label"
                  label="Period"
                  data-cy="purchaseLimitPeriod"
                  value={value ?? 'WEEK'}
                  onChange={onChange}
                >
                  {PURCHASE_LIMIT_PERIOD_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Box>
      </Box>

      <Box className={editClasses.fieldGroup}>
        <Typography className={editClasses.sectionHeading}>
          {optionalLabel('Last day to purchase')}
        </Typography>
        <Typography className={editClasses.fieldHelper}>
          {EXPIRES_AT_DESCRIPTION}
        </Typography>
        <LocalizationProvider
          dateAdapter={CustomLuxonAdapter}
          dateFormats={{ normalDateWithWeekday: 'LLL d, yyyy' }}
        >
          <ThemeProvider theme={datePickerTheme}>
            <Controller
              name="expiresAt"
              control={control}
              defaultValue={null}
              render={({ onChange, value }) => (
                <DesktopDatePicker
                  label="Last day to purchase"
                  name="expiresAt"
                  value={value ?? null}
                  onChange={onChange}
                  disablePast
                  format="LLL d, yyyy"
                  dayOfWeekFormatter={day => `${day}`}
                  closeOnSelect
                  sx={{ width: '100%' }}
                  slots={mergeDesktopDatePickerSlots({
                    openPickerIcon: EventIcon,
                  })}
                  slotProps={mergeDesktopDatePickerSlotProps({
                    textField: {
                      sx: outlinedPickerFieldSx,
                      error: !!errors.expiresAt,
                      helperText: errors.expiresAt?.message || null,
                    },
                  })}
                />
              )}
            />
          </ThemeProvider>
        </LocalizationProvider>
        {expiresAt ? (
          <Button
            type="button"
            size="small"
            color="primary"
            onClick={() =>
              setValue('expiresAt', null, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          >
            Remove date
          </Button>
        ) : null}
      </Box>

      <Box mb={3}>
        <TipWell
          variant="neutral"
          icon={InfoOutlinedIcon}
          caption={VIRTUAL_COACHING_LEGAL_CAPTION}
        />
      </Box>
    </>
  )
}

export default VirtualCoachingSettings
